import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Form,
  Input,
  Tag,
  Popconfirm,
  message,
  Empty,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  PoweroffOutlined,
  ReloadOutlined,
  SafetyOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import {
  useUsersQuery,
  useAvailableEmployeesQuery,
  useRolesQuery,
  useActiveStoresQuery,
  useAllFeaturesQuery,
  useUserOverridesQuery,
} from "../../Services";
import { PageHeader, tablePagination } from "../../components";
import { UserForm } from "./common/UserForm";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { User } from "../../Types";
import { useUserMutation } from "./useUserMutation";

export const UsersPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form] = Form.useForm();
  const [overrides, setOverrides] = useState<Record<string, number>>({});

  // Queries
  const {
    data: usersResponse,
    isLoading,
    refetch,
  } = useUsersQuery({
    page,
    limit: PAGE_SIZE,
    search,
  });
  const users = usersResponse?.data;
  const total = usersResponse?.meta?.total || 0;

  const { data: availableEmployeesData, isLoading: employeeLoading } =
    useAvailableEmployeesQuery({
      enabled: drawerOpen && isCreating,
    });
  const availableEmployees = availableEmployeesData || [];

  const { data: rolesData } = useRolesQuery();
  const roles = rolesData || [];

  const { data: storesData } = useActiveStoresQuery();
  const stores = Array.isArray(storesData)
    ? storesData
    : ((storesData as any)?.stores ?? []);

  const { data: subFeatures } = useAllFeaturesQuery({
    enabled: drawerOpen,
  });

  const { data: userOverrides } = useUserOverridesQuery(
    editingUser?.id ?? 0,
    {
      enabled: drawerOpen && !isCreating && !!editingUser?.id,
    },
  );

  useEffect(() => {
    if (userOverrides && Array.isArray(userOverrides)) {
      const map: Record<string, number> = {};
      userOverrides.forEach((o: any) => {
        map[o.sub_feature_id] = o.is_allowed;
      });
      setOverrides(map);
    }
  }, [userOverrides]);

  const openDrawer = (user?: User, creating = false) => {
    setIsCreating(creating);
    if (user && !creating) {
      setEditingUser(user);
      form.setFieldsValue({
        username: user.username,
        email: user.email,
        role_id: user.role_id,
        store_id: user.store_id,
      });
    } else {
      setEditingUser(null);
      form.resetFields();
      setOverrides({});
      queryClient.invalidateQueries({ queryKey: ["availableEmployees"] });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingUser(null);
    setOverrides({});
    form.resetFields();
  };
  const { submitMutation, toggleStatusMutation } = useUserMutation({
    isCreating,
    editingUser,
    form,
    closeDrawer,
    overrides: Object.entries(overrides)
      .filter(([_, v]) => v !== undefined)
      .map(([subFeatureId, isAllowed]) => ({
        sub_feature_id: subFeatureId,
        is_allowed: isAllowed,
      })),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      submitMutation.mutate(values);
    } catch (error) {
      message.error("Please fill all the required fields.");
    }
  };

  const handleToggleStatus = (user: User) => {
    const newStatus = user.status === "Active" ? "Inactive" : "Active";
    toggleStatusMutation.mutate({ id: user.id, newStatus });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      title: "User",
      key: "user",
      render: (_: any, rec: User) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <span className="text-gray-600 font-semibold text-xs">
              {rec.username[0]?.toUpperCase()}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm m-0 leading-tight">
              {rec.username}
            </p>
            <p className="text-xs text-gray-400 m-0 mt-0.5">{rec.email}</p>
          </div>
        </div>
      ),
    },
    {
      title: "Employee",
      key: "employee",
      render: (_: any, rec: User) =>
        rec.employee_id ? (
          <div>
            <p className="font-medium text-gray-800 text-sm m-0 leading-tight">
              {rec.employee_first_name} {rec.employee_last_name}
            </p>
            <p className="text-xs text-gray-400 m-0 mt-0.5">
              {rec.employee_nic || "No NIC"}
            </p>
          </div>
        ) : (
          <span className="text-gray-300 text-xs">Not linked</span>
        ),
    },
    {
      title: "Role",
      dataIndex: "role_name",
      key: "role_name",
      render: (r: string) => {
        const colors: Record<string, string> = {
          Admin: "purple",
          Manager: "blue",
          Cashier: "cyan",
          Supervisor: "orange",
          Accountant: "gold",
          Labor: "default",
        };
        return <Tag color={colors[r] || "default"}>{r}</Tag>;
      },
    },
    {
      title: "Assigned Stores",
      key: "stores",
      render: (_: any, rec: User) => (
        <div className="flex flex-col gap-1 items-start">
          {rec.store_id && rec.store_name ? (
            <>
              <Tag
                color={rec.store_main_store === 1 ? "purple" : "blue"}
                className="font-semibold rounded-md"
              >
                {rec.store_name}
              </Tag>
              {(rec as any).store_type && (
                <Tag
                  color={(rec as any).store_type === "vehicle" ? "orange" : "cyan"}
                  className="font-semibold rounded-md text-[10px]"
                >
                  {(rec as any).store_type === "vehicle" ? "🚚 Vehicle" : "🏢 Shop"}
                </Tag>
              )}
            </>
          ) : (
            <span className="text-gray-300 text-xs">No stores</span>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => (
        <Tag color={s === "Active" ? "success" : "default"}>{s}</Tag>
      ),
    },
    {
      title: "Joined",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => (
        <span className="text-xs text-gray-400">
          {new Date(v).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, rec: User) => (
        <div className="flex items-center gap-1">
          <Tooltip title="Edit User">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => openDrawer(rec, false)}
            />
          </Tooltip>
          <Popconfirm
            title={`Mark as ${rec.status === "Active" ? "Inactive" : "Active"}?`}
            onConfirm={() => handleToggleStatus(rec)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip
              title={rec.status === "Active" ? "Deactivate" : "Activate"}
            >
              <Button
                type="text"
                icon={<PoweroffOutlined />}
                size="small"
                danger={rec.status === "Active"}
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="User Management"
        subText={`Total: ${total} users`}
        handleButtons={[
          {
            title: "Add User",
            icon: PlusOutlined,
            onClick: () => openDrawer(undefined, true),
          },
          {
            title: "Refresh",
            icon: ReloadOutlined,
            onClick: refetch,
          },
        ]}
        searchBar={
          <Input
            placeholder="Search by username, email, role…"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            onClear={() => {
              setSearchInput("");
              setPage(1);
            }}
            style={{ width: 260 }}
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden">
        <Table
          dataSource={users}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "users",
          })}
          scroll={{ x: 950 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No users found"
              />
            ),
          }}
        />
      </div>

      <UserForm
        open={drawerOpen}
        onClose={closeDrawer}
        onSubmit={handleSubmit}
        loading={submitMutation.isPending}
        editingUser={editingUser}
        isCreating={isCreating}
        form={form}
        availableEmployees={availableEmployees}
        employeeLoading={employeeLoading}
        roles={roles}
        stores={stores}
        subFeatures={subFeatures}
        overrides={overrides}
        onOverridesChange={setOverrides}
      />
    </div>
  );
};
